import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import getConfig from '../temp/config';

type GoogleRecaptchaManager = {
  googleRecaptchaV3SiteKey: string;
  refreshRecaptchaToken: () => void;
  requestRecaptchaRefresh: (validationErrorFields?: string[]) => void;
  registerRefreshRecaptchaHandler: (handler: () => void) => void;
  setFormName: (name: string) => void;
  formName: string;
  refreshRequestId: number;
  refreshValidationErrorFields: string[];
};

interface GoogleRecaptchaManagerProviderProps {
  googleRecaptchaV3SiteKey: string;
  children: ReactNode;
}

const { googleRecaptchaV3SiteKey } = getConfig();
export const GoogleRecaptchaManagerContext = createContext<GoogleRecaptchaManager>({
  googleRecaptchaV3SiteKey,
  refreshRecaptchaToken: () => {},
  requestRecaptchaRefresh: () => {},
  registerRefreshRecaptchaHandler: () => {},
  setFormName: () => {},
  formName: 'form',
  refreshRequestId: 0,
  refreshValidationErrorFields: [],
});

export const useGoogleRecaptchaManager = () => {
  return useContext(GoogleRecaptchaManagerContext);
};

export const GoogleRecaptchaManagerProvider: React.FC<GoogleRecaptchaManagerProviderProps> = ({
  googleRecaptchaV3SiteKey,
  children,
}) => {
  const [refreshHandler, setRefreshHandler] = useState<(() => void) | null>(null);
  const [formName, setFormName] = useState<string>('form');
  const [refreshRequestId, setRefreshRequestId] = useState(0);
  const [refreshValidationErrorFields, setRefreshValidationErrorFields] = useState<string[]>([]);

  const registerRefreshRecaptchaHandler = useCallback((handler: () => void) => {
    setRefreshHandler(() => handler);
  }, []);

  const refreshRecaptchaToken = useCallback(() => {
    if (!refreshHandler) return;

    refreshHandler();
  }, [refreshHandler]);

  const requestRecaptchaRefresh = useCallback((validationErrorFields: string[] = []) => {
    setRefreshValidationErrorFields(validationErrorFields);
    setRefreshRequestId((currentRequestId) => currentRequestId + 1);
  }, []);

  return (
    <GoogleRecaptchaManagerContext.Provider
      value={{
        googleRecaptchaV3SiteKey,
        refreshRecaptchaToken,
        requestRecaptchaRefresh,
        registerRefreshRecaptchaHandler,
        setFormName,
        formName,
        refreshRequestId,
        refreshValidationErrorFields,
      }}
    >
      {children}
    </GoogleRecaptchaManagerContext.Provider>
  );
};
